<template>
  <q-dialog
    :model-value="modelValue"
    persistent
    maximized
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card class="tender-contractor-dialog">
      <!-- Dialog Header -->
      <q-card-section class="dialog-header bg-primary">
        <div class="row items-center">
          <div class="col">
            <div class="text-h5 text-weight-bold text-white">
              {{ isEdit ? 'Edit Contractor' : 'Add Contractor' }}
            </div>
            <div class="text-body2 text-blue-2">
              {{ isEdit ? 'Update contractor information' : 'Add a new contractor to this tender' }}
            </div>
          </div>
          <div class="col-auto">
            <q-btn
              flat
              round
              icon="close"
              size="md"
              text-color="white"
              @click="onCancel"
            />
          </div>
        </div>
      </q-card-section>

      <!-- Dialog Content -->
      <q-card-section class="dialog-content">
        <q-form ref="contractorForm" class="q-gutter-lg" @submit="onSubmit">

          <!-- Contractor Selection -->
          <div class="form-section">
            <div class="section-header">
              <q-icon name="business" color="primary" size="24px" class="q-mr-sm" />
              <div class="section-title">Contractor Information</div>
            </div>

            <div class="section-content">
              <div class="row q-col-gutter-md">
                <!-- Organization/Contractor Selection -->
                <div class="col-12 col-md-6">
                  <q-select
                    v-model="form.organization_id"
                    :options="contractorOptions"
                    label="Contractor *"
                    outlined
                    emit-value
                    map-options
                    :readonly="!!contractor"
                    :loading="loadingContractors"
                    :rules="[val => !!val || 'Contractor is required']"
                    class="enhanced-select"
                    @update:model-value="onContractorChange"
                  >
                    <template #prepend>
                      <q-icon name="business" />
                    </template>
                    <template #no-option>
                      <q-item>
                        <q-item-section class="text-grey">
                          No contractors available. Please create an organization with type 'contractor' first.
                        </q-item-section>
                      </q-item>
                    </template>
                    <template #after>
                      <q-btn
                        round
                        dense
                        flat
                        icon="add"
                        @click="showCreateOrganizationDialog"
                      >
                        <q-tooltip>Create New Contractor</q-tooltip>
                      </q-btn>
                    </template>
                  </q-select>
                </div>

                <!-- Contact Selection -->
                <div class="col-12 col-md-6">
                  <q-select
                    v-model="form.organization_contact_id"
                    :options="contactOptions"
                    label="Primary Contact"
                    outlined
                    emit-value
                    map-options
                    clearable
                    :disable="!form.organization_id"
                    class="enhanced-select"
                    hint="Select the contact who will receive tender emails"
                  >
                    <template #prepend>
                      <q-icon name="person" />
                    </template>
                    <template #no-option>
                      <q-item>
                        <q-item-section class="text-grey">
                          {{ form.organization_id ? 'No contacts available for this contractor' : 'Select a contractor first' }}
                        </q-item-section>
                      </q-item>
                    </template>
                    <template #after>
                      <q-btn
                        round
                        dense
                        flat
                        icon="add"
                        :disable="!form.organization_id"
                        @click="showAddContactDialog"
                      >
                        <q-tooltip>Add Contact</q-tooltip>
                      </q-btn>
                    </template>
                  </q-select>
                </div>
              </div>
            </div>
          </div>

          <!-- Tender Settings -->
          <div class="form-section">
            <div class="section-header">
              <q-icon name="settings" color="secondary" size="24px" class="q-mr-sm" />
              <div class="section-title">Tender Settings</div>
            </div>

            <div class="section-content">
              <div class="row q-col-gutter-md">
                <!-- Primary Contractor -->
                <div class="col-12 col-md-6">
                  <q-checkbox
                    v-model="form.is_primary"
                    label="Primary Contractor"
                    color="primary"
                  />
                  <div class="field-hint">
                    Mark this contractor as the primary/preferred contractor for this tender
                  </div>
                </div>

                <!-- Status -->
                <div class="col-12 col-md-6">
                  <q-select
                    v-model="form.status"
                    :options="statusOptions"
                    label="Status"
                    outlined
                    emit-value
                    map-options
                    class="enhanced-select"
                  >
                    <template #prepend>
                      <q-icon name="flag" />
                    </template>
                  </q-select>
                </div>
              </div>
            </div>
          </div>

          <!-- Notes -->
          <div class="form-section">
            <div class="section-header">
              <q-icon name="note" color="accent" size="24px" class="q-mr-sm" />
              <div class="section-title">Notes</div>
            </div>

            <div class="section-content">
              <q-input
                v-model="form.notes"
                label="Notes"
                outlined
                type="textarea"
                autogrow
                rows="3"
                hint="Any additional notes or special instructions for this contractor"
                class="enhanced-input"
              >
                <template #prepend>
                  <q-icon name="note" />
                </template>
              </q-input>
            </div>
          </div>
        </q-form>
      </q-card-section>

      <!-- Dialog Actions -->
      <q-card-actions class="dialog-actions bg-grey-1">
        <div class="row full-width items-center">
          <div class="col">
            <q-btn
              flat
              icon="help"
              label="Help"
              color="grey-7"
              @click="showHelp"
            />
          </div>
          <div class="col-auto">
            <q-btn-group>
              <q-btn
                flat
                label="Cancel"
                color="grey-7"
                class="action-btn"
                @click="onCancel"
              />
              <q-btn
                :label="isEdit ? 'Update Contractor' : 'Add Contractor'"
                color="primary"
                :loading="savingContractor"
                class="action-btn action-btn--primary"
                @click="onSubmit"
              />
            </q-btn-group>
          </div>
        </div>
      </q-card-actions>
    </q-card>

    <!-- Help Dialog -->
    <q-dialog v-model="showHelpDialog">
      <q-card style="width: 600px; max-width: 80vw">
        <q-card-section>
          <div class="text-h6">Contractor Management Help</div>
        </q-card-section>
        <q-card-section>
          <q-list>
            <q-item>
              <q-item-section avatar>
                <q-icon name="business" color="primary" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Contractor Selection</q-item-label>
                <q-item-label caption>
                  Choose the contractor from organizations with type 'contractor'.
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item>
              <q-item-section avatar>
                <q-icon name="person" color="secondary" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Primary Contact</q-item-label>
                <q-item-label caption>
                  Select the specific contact person who will receive tender communications.
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item>
              <q-item-section avatar>
                <q-icon name="flag" color="accent" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Status Management</q-item-label>
                <q-item-label caption>
                  Track contractor status from pending through won/lost with detailed information.
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Close" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { organizationService } from '../../../services/organizationService';
  import { useQuasar } from 'quasar';

  const props = defineProps({
    modelValue: {
      type: Boolean,
      required: true,
    },
    tenderId: {
      type: Number,
      required: true,
    },
    contractor: {
      type: Object,
      default: null,
    },
    existingOrganizationIds: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits(['update:modelValue', 'save', 'cancel']);

  const $q = useQuasar();

  // Refs
  const contractorForm = ref(null);
  const showHelpDialog = ref(false);
  const savingContractor = ref(false);

  // Local state for contractors (organizations with type='contractor')
  const contractors = ref([]);
  const loadingContractors = ref(false);

  // Form state (using organization_id and organization_contact_id)
  const form = ref({
    organization_id: null,
    organization_contact_id: null,
    is_primary: false,
    status: 'unknown',
    win_date: '',
    loss_date: '',
    notes: '',
  });

  // Options
  const statusOptions = [
    { label: 'DNQ', value: 'dnq' },
    { label: 'BnB', value: 'bnb' },
    { label: 'Unknown', value: 'unknown' },
    { label: 'Lost', value: 'lost' },
    { label: 'In Negotiation', value: 'in_negotiation' },
    { label: 'Won', value: 'won' },
    { label: 'Withdrew', value: 'withdrew' },
  ];

  // Computed properties
  const isEdit = computed(() => !!props.contractor);

  const contractorOptions = computed(() => {
    return contractors.value
      .filter((org) => {
        // When editing, always show the current contractor
        if (isEdit.value && org.id === form.value.organization_id) {return true;}
        // Hide already-added contractors
        return !props.existingOrganizationIds.includes(org.id);
      })
      .map((org) => ({
        label: org.name,
        value: org.id,
      }));
  });

  const contactOptions = computed(() => {
    if (!form.value.organization_id) {return [];}

    const contractor = contractors.value.find((c) => c.id === form.value.organization_id);
    if (!contractor || !contractor.contacts) {return [];}

    return contractor.contacts.map((contact) => ({
      label: `${contact.name} (${contact.email})`,
      value: contact.id,
    }));
  });

  // Methods
  function resetForm() {
    form.value = {
      organization_id: null,
      organization_contact_id: null,
      is_primary: false,
      status: 'unknown',
      win_date: '',
      loss_date: '',
      notes: '',
    };
  }

  function populateForm(contractor) {
    if (contractor.pivot) {
      form.value = {
        organization_id: contractor.pivot.organization_id || contractor.id,
        organization_contact_id: contractor.pivot.organization_contact_id || contractor.pivot.contact_id || null,
        is_primary: contractor.pivot.is_primary || false,
        status: contractor.pivot.status || 'unknown',
        win_date: contractor.pivot.win_date || '',
        loss_date: contractor.pivot.loss_date || '',
        notes: contractor.pivot.notes || '',
      };
    } else {
      form.value = {
        organization_id: contractor.organization_id || contractor.contractor_id || contractor.id || null,
        organization_contact_id: contractor.organization_contact_id || contractor.contact_id || null,
        is_primary: contractor.is_primary || false,
        status: contractor.status || 'unknown',
        win_date: contractor.win_date || '',
        loss_date: contractor.loss_date || '',
        notes: contractor.notes || '',
      };
    }
  }

  function onContractorChange() {
    // Clear contact selection when contractor changes
    form.value.organization_contact_id = null;
  }

  async function onSubmit() {
    const isValid = await contractorForm.value.validate();
    if (!isValid) {return;}

    savingContractor.value = true;
    try {
      const contractorData = {
        tender_id: props.tenderId,
        organization_id: form.value.organization_id,
        organization_contact_id: form.value.organization_contact_id,
        is_primary: form.value.is_primary,
        status: form.value.status,
        win_date: form.value.win_date || null,
        loss_date: form.value.loss_date || null,
        notes: form.value.notes || null,
      };

      emit('save', contractorData);
    } catch (error) {
      $q.notify({
        color: 'negative',
        message: `Failed to ${isEdit.value ? 'update' : 'add'} contractor: ${error.message}`,
        icon: 'warning',
      });
    } finally {
      savingContractor.value = false;
    }
  }

  function onCancel() {
    emit('cancel');
  }

  function showCreateOrganizationDialog() {
    $q.notify({
      color: 'info',
      message: 'Redirecting to organization creation...',
      timeout: 2000,
    });

    // Navigate to organizations page where contractors can be created
    // TODO: Update this URL when organizations management page exists
    const organizationCreateUrl = '/settings?tab=organizations';
    window.open(organizationCreateUrl, '_blank');
  }

  function showAddContactDialog() {
    if (!form.value.organization_id) {return;}

    $q.notify({
      color: 'info',
      message: 'Redirecting to organization contact management...',
      timeout: 2000,
    });

    // Navigate to organization detail page where contacts can be added
    // TODO: Update this URL when organization detail page exists
    const organizationDetailUrl = `/settings?tab=organizations&id=${form.value.organization_id}`;
    window.open(organizationDetailUrl, '_blank');
  }

  function showHelp() {
    showHelpDialog.value = true;
  }

  async function fetchContractors() {
    loadingContractors.value = true;
    try {
      const response = await organizationService.getContractors({ no_pagination: true });
      // Handle different response formats
      contractors.value = response.data || response || [];
    } catch (error) {
      console.error('Failed to load contractors:', error);
      $q.notify({
        color: 'negative',
        message: 'Failed to load contractors',
        icon: 'warning',
      });
    } finally {
      loadingContractors.value = false;
    }
  }

  // Watchers
  watch(() => props.modelValue, (newVal) => {
    if (newVal) {
      // Dialog opened
      if (props.contractor) {
        populateForm(props.contractor);
      } else {
        resetForm();
      }
    }
  });

  // Lifecycle hooks
  onMounted(async () => {
    if (props.contractor) {
      populateForm(props.contractor);
    }

    // Fetch contractors (organizations with type='contractor')
    await fetchContractors();
  });
</script>

<style lang="scss" scoped>
.tender-contractor-dialog {
  height: 100vh;
  display: flex;
  flex-direction: column;

  .dialog-header {
    flex-shrink: 0;
    border-radius: 0;
  }

  .dialog-content {
    flex: 1;
    overflow-y: auto;
    padding: 24px;

    .form-section {
      margin-bottom: 32px;

      .section-header {
        display: flex;
        align-items: center;
        margin-bottom: 16px;
        padding-bottom: 8px;
        border-bottom: 2px solid #f0f0f0;

        .section-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #333;
        }
      }

      .section-content {
        .enhanced-input,
        .enhanced-select {
          :deep(.q-field__control) {
            border-radius: 8px;
          }

          :deep(.q-field__prepend) {
            color: #666;
          }
        }

        .field-hint {
          font-size: 0.8rem;
          color: #666;
          margin-top: 4px;
          line-height: 1.3;
        }
      }
    }
  }

  .dialog-actions {
    flex-shrink: 0;
    padding: 16px 24px;
    border-top: 1px solid #e0e0e0;

    .action-btn {
      min-width: 120px;
      font-weight: 600;
      border-radius: 6px;
      padding: 8px 16px;

      &--primary {
        background: linear-gradient(45deg, #1976d2, #42a5f5);
        color: white;
      }
    }
  }
}
</style>
